/* ============================================================
   useToasts — transient toast queue (shared singleton)

   Replaces the prototype's imperative toast() DOM helper with a
   reactive list the Toasts component renders. Auto-dismiss at
   2400 ms, matching the prototype.
   ============================================================ */

import { ref, readonly } from 'vue'

/** An optional inline action on a toast (e.g. "Undo" after a delete). */
export interface ToastAction {
  label: string
  onAction: () => void
}

export interface Toast {
  id: number
  msg: string
  kind: 'info' | 'error'
  action?: ToastAction
}

const toasts = ref<Toast[]>([])
let nextId = 1

/** Auto-dismiss timing: action toasts linger longer so the user can react. */
const DEFAULT_MS = 2400
const ACTION_MS = 6000

/** Remove a toast by id (used by the action button and the auto-dismiss timer). */
export function dismissToast(id: number): void {
  toasts.value = toasts.value.filter((t) => t.id !== id)
}

export function pushToast(
  msg: string,
  kind: 'info' | 'error' = 'info',
  action?: ToastAction,
): void {
  const id = nextId++
  toasts.value.push({ id, msg, kind, action })
  setTimeout(() => dismissToast(id), action ? ACTION_MS : DEFAULT_MS)
}

export function useToasts() {
  return {
    toasts: readonly(toasts),
    pushToast,
    dismissToast,
  }
}
